<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";

import { useActionStore } from "./actions.store";

import PopupDialog from "@core/ui/PopupDialog.vue";
import ConditionLabel from "../condition/ConditionLabel.vue";

interface Props {
  actionId: number;
  conditionIds: number[];
  maxOccurrencesReached: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits(["close"]);

const okButton = ref<HTMLButtonElement>();

const actionStore = useActionStore();

const action = computed(() => actionStore.items[props.actionId]);

onMounted(() => {
  nextTick(() => {
    okButton.value?.focus();
  });
});
</script>

<template>
  <popup-dialog
    :title="$t('errors.conditionNotMet')"
    :width="410"
    class="action-unfulfilled-message"
  >
    <div class="p-5 action-unfulfilled-message__body">
      <div v-if="maxOccurrencesReached">
        {{
          $t("errors.$action$playsExceeded", {
            title: action.title,
            maxOccurrences: action.maxOccurrences
          })
        }}
      </div>
      <div v-else>
        <i18n-t keypath="texts.actionUnplayable" tag="div" scope="global">
          <template #action>
            <em>{{ action.title }}</em>
          </template>
        </i18n-t>

        <h3 class="label mt-4">
          {{ $t("headings.unfulfilledConditions") }}{{ $t(":") }}
        </h3>
        <ul class="action-unfulfilled-message__list">
          <li v-for="conditionId in props.conditionIds" :key="conditionId">
            <condition-label :condition-id="conditionId" />
          </li>
        </ul>
      </div>

      <div class="action-unfulfilled-message__buttons mt-4">
        <button
          ref="okButton"
          type="button"
          class="button"
          @click.prevent="emit('close')"
        >
          {{ $t("labels.ok") }}
        </button>
      </div>
    </div>
  </popup-dialog>
</template>

<style lang="scss">
.action-unfulfilled-message {
  border-radius: 0.5rem;

  .popup__label {
    background-color: var(--color-error-light);
  }

  &__body {
    margin: auto;
  }
  &__list {
    margin: 0.5rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 1.25rem;
  }
  &__buttons {
    display: flex;
    justify-content: center;
  }
}
</style>
